import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { AiTrackingOrigin } from '@databox/types/domains/ai/chat.enums';

import type { Service } from 'webapp-types';

import makeArray from 'webapp/helpers/make-array';
import didUpdate from 'webapp/modifiers/did-update';
import BillingForbiddenFeatureComponent from 'webapp/components/billing/forbidden-feature';
import CommonGenieInputComponent from 'webapp/components/common/genie/input';
import CommonTypewriterComponent from 'webapp/components/common/typewriter';
import CommonUiIcon from 'webapp/components/common/ui-icon';
import DataPerformanceInsightsSanitizedComponent from 'webapp/components/data-performance/insights/sanitized';
import DataPerformanceSummaryBaseComponent, {
	type Args as BaseArgs,
} from 'webapp/components/data-performance/summary/base';
import DataPerformanceSummaryIndicatorComponent from 'webapp/components/data-performance/summary/indicator';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import ToolTipComponent from 'webapp/components/tool-tip';

import DataPerformanceSummaryBoard from 'webapp/models-custom/data-performance-summary/board';
import MetricSetup from 'webapp/models-custom/metric-setup';

interface Args extends BaseArgs {
	summariesLoading?: boolean;
	hideGenieInput?: boolean;
	closeFn?: () => void;
	onContentChange?: () => void;
}

export default class DataPerformanceSummaryIndexComponent extends DataPerformanceSummaryBaseComponent<Args> {
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked isSummaryTextCollapsed = true;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
	}

	override get summariesLoading() {
		return this.args.summariesLoading || super.summariesLoading;
	}

	get showForecastSection() {
		return this.args.model.canRequestForecast && !this.args.model.errorLoadingForecast;
	}

	get hasSummaryTextMultipleParagraphs() {
		const summary = this.args.model.sanitizedSummary;

		return summary && (String(summary.text).match(/<\/p>/gim) || []).length > 1; // Count closing tags, cause opening might have classes and so on
	}

	get showExpandSummaryBtn() {
		return this.isSummaryTextCollapsed && this.hasSummaryTextMultipleParagraphs;
	}

	@action
	override scheduleOnContentChange() {
		if (this.args.onContentChange) {
			scheduleOnce('afterRender', this, this.args.onContentChange);
		}
	}

	@action
	viewInsights() {
		const metricSetupData = this.args.model.metricSetupData;

		if (metricSetupData) {
			const metricSetup = new MetricSetup(metricSetupData);
			this.metricDetailsService.openMetricDetailsDrawer(
				{
					metricSetup,
					dateRange: this.args.model.dateRangeFormula,
				},
				'performance_tooltip',
			);
		}
	}

	get showGenieInput(): boolean {
		return (
			this.aiChatService.isEligibleForAiChat &&
			!this.summariesLoading &&
			!this.args.hideGenieInput
		);
	}

	@action
	submitPrompt(promptText: string) {
		const model = this.args.model;
		let origin = AiTrackingOrigin.METRIC_AI_SUMMARY;

		if (model instanceof DataPerformanceSummaryBoard) {
			origin = AiTrackingOrigin.DATABOARD_AI_SUMMARY;
		}

		this.aiChatService.submitPromptFromAiSummary({
			model,
			promptText,
			origin,
		});
	}

	@action
	expandSummary() {
		this.isSummaryTextCollapsed = false; // Not reset again;
	}

	@action
	onFeatureChange() {
		const oldValue = this.generativeAIEnabled,
			newValue = !!this.args.model.generativeAIEnabled;

		if (oldValue !== newValue) {
			this.generativeAIEnabled = !!this.args.model?.generativeAIEnabled;
			this.args.model.checkAndLoadAdditionalData();
			this.scheduleOnContentChange();
		}
	}

	<template>
		<div {{didUpdate this.onFeatureChange @model.generativeAIEnabled}}></div>
		{{#if @model.generativeAIEnabled}}
			<div
				class="data-performance-summary {{if @model.isTypeDynamic 'dynamic-summary'}}"
				{{didUpdate
					this.scheduleOnContentChange
					this.summariesLoading
					@model.forecastLoading
				}}
			>
				{{#if @model.noSummaryData}}
					{{! ERROR STATE }}
					<div class="scrollable">
						<div class="summary-error">
							<CommonUiIcon @name="Alert" @size="lg" />
							<p class="summary-error__title">Unable to load</p>
							<p class="summary-error__description">
								{{#if @model.isSummariesDataEmpty}}
									Currently unavailable due to incomplete data.
								{{else if this.limitReached}}
									{{this.limitReachedMessage}}
								{{else}}
									We are unable to load the performance summary at the moment.
									Please give it another try shortly. If the issue persist, please
									contact team.
								{{/if}}
							</p>
						</div>

						{{#if this.canReloadSummaries}}
							<div class="view-more-insights view-more-insights--small">
								<a
									role="button"
									class="button"
									{{on "click" @model.reloadDataPerformanceSummaries}}
								>
									<CommonUiIcon @name="Refresh" @size="sm" />
									Try again
								</a>
							</div>
						{{/if}}
					</div>
				{{else}}
					{{! LOADED STATE }}
					{{#if @model.isNotTypeDynamic}}
						{{#if @model.object}}
							<div class="header">
								<div class="header-content">
									<div class="header-info">
										<EditableModelPropertyComponent
											@model={{@model}}
											@canEdit={{false}}
										/>
										{{#if this.viewInsights}}
											{{#if @model.metricSetupData}}
												{{#unless @model.isTypeDynamic}}
													<p>
														AI summary •
														<a
															role="button"
															class="link link--secondary"
															{{on "click" this.viewInsights}}
														>
															View details
														</a>
													</p>
												{{/unless}}
											{{/if}}
										{{/if}}
									</div>
								</div>
								{{#if @closeFn}}
									<a role="button" class="action-close" {{on "click" @closeFn}}>
										<CommonUiIcon @name="Close" @size="md" />
									</a>
								{{/if}}
							</div>
						{{/if}}
					{{/if}}

					<div class="scrollable">
						{{! Trend indicator }}
						{{#if @model.trend}}
							{{#if this.summariesLoading}}
								<div class="loader">
									<div class="line chunky trend--disabled">
										<CommonUiIcon
											@name="PerformanceUpOld"
											@size="xl"
											@class="data-performance-summary-trend"
										/>
										<span class="line"></span>
									</div>
								</div>
							{{else}}
								<DataPerformanceSummaryIndicatorComponent
									@trend={{@model.trend}}
									@text={{@model.trendDescription}}
								/>
							{{/if}}
						{{/if}}

						{{! Summary text }}
						<div class="summary-container">
							{{#if this.summariesLoading}}
								<div class="loader">
									{{#each (makeArray length=3)}}
										<div class="line"></div>
									{{/each}}
								</div>
							{{else}}
								<div class={{if this.isSummaryTextCollapsed "first-only"}}>
									{{@model.sanitizedSummary.text}}
								</div>
								{{#if this.showExpandSummaryBtn}}
									<div
										role="button"
										class="view-more-btn"
										{{on "click" this.expandSummary}}
									>
										View more<CommonUiIcon @name="ChevronDown" @size="sm" />
									</div>
								{{/if}}
							{{/if}}
						</div>

						{{#if this.summariesLoading}}
							{{#if @model.isNotTypeDynamic}}
								<CommonTypewriterComponent
									@messages={{this.loadingMessages}}
									@intervalTime={{this.loadingMessageIntervalTime}}
								/>
							{{/if}}
						{{/if}}

						{{! Forecast section }}
						{{#if this.showForecastSection}}
							<div class="forecasting-container">
								{{#if @model.forecastLoading}}
									<div class="loader">
										{{#each (makeArray length=3)}}
											<div class="line"></div>
										{{/each}}
									</div>
								{{else}}
									<div>
										TODO FORECAST
									</div>
								{{/if}}
							</div>
						{{/if}}

						{{#unless this.summariesLoading}}
							{{#if @model.activeSummary.insights}}
								<div class="insights">
									<p>Insights</p>

									<DataPerformanceInsightsSanitizedComponent
										@model={{@model.activeSummary.insights}}
									/>
								</div>
							{{/if}}
						{{/unless}}

						{{! Footer (scrolls with content) }}
						{{#if @model.canRequestSummaries}}
							{{#if @model.activeSummary.incompleteData}}
								<div class="footer">
									<div class="footer__actions">
										<span class="sync-warning">
											<CommonUiIcon @name="Alert" @size="sm" />
											<ToolTipComponent
												@text="This performance summary might be incomplete due to data synchronization delays."
												@maxWidth={{200}}
											/>
										</span>
									</div>
								</div>
							{{/if}}
						{{/if}}
					</div>

					{{! Sticky CTA at bottom }}
					{{#if this.showGenieInput}}
						<div class="sticky">
							<CommonGenieInputComponent
								@placeholder="Analyze this with Genie..."
								@onSubmit={{this.submitPrompt}}
							/>
						</div>
					{{/if}}
				{{/if}}
			</div>
		{{else}}
			<div class="data-performance-summary {{if @model.isTypeDynamic 'dynamic-summary'}}">
				<div class="scrollable">
					<div class="summary-locked">
						<CommonUiIcon @name="Lock" @size="lg" />
						<p class="summary-locked__title">
							Get a quick summary of how you're performing
						</p>
						<p class="summary-locked__description">
							Get a summary of your performance generated automatically by AI, so you
							always know how you're progressing.
						</p>
						<BillingForbiddenFeatureComponent
							@feature={{this.feature}}
							@isTextOnly={{true}}
						/>
					</div>
				</div>
			</div>
		{{/if}}
	</template>
}
